import { api, ApiError } from "@/lib/api/client";
import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";
import type { components } from "@/types/api";

export const CHALLENGE_REQUIRED_CODE = "challenge_required";
export const EMAIL_NOT_VERIFIED_CODE = "email_not_verified";
export const VERIFICATION_EMAIL_COOLDOWN_CODE = "verification_email_cooldown";
export const TOO_MANY_VERIFICATION_EMAIL_REQUESTS_CODE = "too_many_verification_email_requests";
const LEGACY_EMAIL_NOT_VERIFIED_MESSAGE = "Please verify your email address before logging in.";

export function isEmailNotVerifiedError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  if (error.code) return error.code === EMAIL_NOT_VERIFIED_CODE;
  return error.rawError === LEGACY_EMAIL_NOT_VERIFIED_MESSAGE;
}

export type LoginRequest = components["schemas"]["LoginRequest"] & {
  challengeToken?: string;
};
export type LoginResponse = components["schemas"]["LoginResponse"];
export type MfaRequiredResponse = components["schemas"]["MfaRequiredResponse"];
export type LoginResult = LoginResponse | MfaRequiredResponse;
export type MfaChallengeResponse = components["schemas"]["MfaChallengeResponse"];
export type MfaRecoveryResponse = components["schemas"]["MfaRecoveryResponse"];

export function isMfaRequired(result: LoginResult): result is MfaRequiredResponse {
  return "mfaRequired" in result && result.mfaRequired === true;
}
export type RegisterRequest = components["schemas"]["RegisterRequest"];
export type RegisterResponse = components["schemas"]["RegisterResponse"];
export type ForgotPasswordRequest = components["schemas"]["ForgotPasswordRequest"];
export type ResetPasswordRequest = components["schemas"]["ResetPasswordRequest"];
export type VerifyEmailRequest = components["schemas"]["VerifyEmailRequest"];
export type VerifyEmailResponse = components["schemas"]["VerifyEmailResponse"];
export type ResendVerificationResponse = components["schemas"]["ResendVerificationResponse"];

export interface MessageResponse {
  message: string;
}

export interface AuthenticatedResponse {
  authenticated: boolean;
}

export function loginUser(body: LoginRequest): Promise<LoginResult> {
  return api.post<LoginResult>("/v1/auth/login", body, { anonymous: true, retrySafe: true });
}

export function mfaChallenge(pendingToken: string): Promise<MfaChallengeResponse> {
  return api.post<MfaChallengeResponse>(
    "/v1/auth/mfa/challenge",
    { pendingToken },
    { anonymous: true, retrySafe: true },
  );
}

export function mfaVerify(
  pendingToken: string,
  response: AuthenticationResponseJSON,
): Promise<LoginResponse> {
  return api.post<LoginResponse>(
    "/v1/auth/mfa/verify",
    { pendingToken, response },
    { anonymous: true, retrySafe: true },
  );
}

export function mfaRecovery(pendingToken: string, code: string): Promise<MfaRecoveryResponse> {
  return api.post<MfaRecoveryResponse>(
    "/v1/auth/mfa/recovery",
    { pendingToken, code },
    { anonymous: true },
  );
}

export function mfaTotp(pendingToken: string, code: string): Promise<LoginResponse> {
  return api.post<LoginResponse>(
    "/v1/auth/mfa/totp",
    { pendingToken, code },
    { anonymous: true, retrySafe: true },
  );
}

export function passwordlessChallenge(): Promise<MfaChallengeResponse> {
  return api.post<MfaChallengeResponse>("/v1/auth/passkey/challenge", undefined, {
    anonymous: true,
    retrySafe: true,
  });
}

export function passwordlessLogin(response: AuthenticationResponseJSON): Promise<LoginResponse> {
  return api.post<LoginResponse>(
    "/v1/auth/passkey/login",
    { response },
    { anonymous: true, retrySafe: true },
  );
}

export function registerUser(body: RegisterRequest): Promise<RegisterResponse> {
  return api.post<RegisterResponse>("/v1/auth/register", body, {
    anonymous: true,
  });
}

export function forgotPassword(body: ForgotPasswordRequest): Promise<MessageResponse> {
  return api.post<MessageResponse>("/v1/auth/forgot-password", body, {
    anonymous: true,
  });
}

export function resetPassword(token: string, password: string): Promise<MessageResponse> {
  return api.post<MessageResponse>(
    "/v1/auth/reset-password",
    { token, password } satisfies ResetPasswordRequest,
    { anonymous: true },
  );
}

export function completeEmailVerification(token: string): Promise<VerifyEmailResponse> {
  return api.post<VerifyEmailResponse>(
    "/v1/auth/verify-email",
    { token } satisfies VerifyEmailRequest,
    { anonymous: true },
  );
}

export function resendVerification(email: string): Promise<ResendVerificationResponse> {
  return api.post<ResendVerificationResponse>(
    "/v1/auth/resend-verification",
    { email },
    { anonymous: true },
  );
}

export function checkAuthenticated(): Promise<AuthenticatedResponse> {
  return api.get<AuthenticatedResponse>("/v1/authenticated");
}

export function revokeCurrentSession(): Promise<void> {
  return api.delete<void>("/v1/sessions/current", { keepalive: true });
}
